import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  Check,
  RotateCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { useBoBInstallationEvents } from "@/hooks/useBoBInstallationEvents";
import { analytics } from "@/lib/analytics";
import { PostInstallGuidance } from "./PostInstallGuidance";
import { useInstallationProgress } from "@/hooks/useInstallationProgress";

interface BoBInstallProgressProps {
  instanceId: string;
  onClose: () => void;
  onRetry?: (bobName: string) => void;
}

interface InstallationState {
  bobName: string;
  state: string;
  progress: number;
  error?: string;
  bytesDownloaded?: number;
  totalBytes?: number;
}

export function BoBInstallProgress({
  instanceId,
  onClose,
  onRetry,
}: BoBInstallProgressProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { events, isConnected, error: connectionError} = useBoBInstallationEvents(instanceId);

  // P0-PM-001: Background installation tracking
  const { updateInstallation, removeInstallation } = useInstallationProgress();

  const [installations, setInstallations] = useState<Map<string, InstallationState>>(
    new Map()
  );
  const [canContinueInBackground, setCanContinueInBackground] = useState(false);
  const [userDismissedModal, setUserDismissedModal] = useState(false);
  // P0-PM-005: Post-install guidance state
  const [showGuidance, setShowGuidance] = useState(false);
  const [guidanceBobName, setGuidanceBobName] = useState<string>('');

  const handleRetry = (bobName: string) => {
    // Clear error and reset state to queued
    setInstallations((prev) => {
      const next = new Map(prev);
      const existing = next.get(bobName);
      if (existing) {
        next.set(bobName, {
          ...existing,
          state: "queued",
          progress: 0,
          error: undefined,
        });
      }
      return next;
    });

    // Notify parent to re-trigger installation
    if (onRetry) {
      onRetry(bobName);
    }
  };

  // P0-PM-002: Process SSE events with toast notifications and cache invalidation
  useEffect(() => {
    events.forEach((event) => {
      if (
        event.type === "progress_update" ||
        event.type === "status_change"
      ) {
        const { bob_name, state, progress, bytes_downloaded, total_bytes, error } =
          event.data;

        setInstallations((prev) => {
          const next = new Map(prev);
          const existing = next.get(bob_name) || {
            bobName: bob_name,
            state: "queued",
            progress: 0,
          };

          const updated = {
            ...existing,
            state: state || existing.state,
            progress: progress !== undefined ? progress : existing.progress,
            bytesDownloaded: bytes_downloaded,
            totalBytes: total_bytes,
            error: error,
          };

          next.set(bob_name, updated);

          // P0-PM-001: Persist to background tracking
          updateInstallation({
            instanceId,
            bobName: bob_name,
            state: updated.state as any,
            progress: updated.progress,
            error: updated.error,
            startedAt: existing.startedAt || Date.now(),
            updatedAt: Date.now(),
          });

          return next;
        });

        // P0-PM-002: Show error toast for failures
        if (state === "failed" && error) {
          toast.error(`Failed to install ${bob_name}`, {
            description: error || 'An error occurred during installation.',
            action: onRetry ? {
              label: 'Retry',
              onClick: () => onRetry(bob_name)
            } : undefined
          });

          // P0-PM-004: Track installation failure
          analytics.track('BoB Installation Failed', {
            bobName: bob_name,
            error,
            stage: state,
            instanceId,
          });
        }
      }

      if (event.type === "complete") {
        const { bob_name } = event.data;
        setInstallations((prev) => {
          const next = new Map(prev);
          const existing = next.get(bob_name);
          if (existing) {
            next.set(bob_name, {
              ...existing,
              state: "installed",
              progress: 100,
            });

            // P0-PM-001: Update background tracking to installed state
            updateInstallation({
              instanceId,
              bobName: bob_name,
              state: 'installed',
              progress: 100,
              startedAt: existing.startedAt || Date.now(),
              updatedAt: Date.now(),
            });

            // Remove from background tracking after 5 seconds
            setTimeout(() => {
              removeInstallation(instanceId, bob_name);
            }, 5000);
          }
          return next;
        });

        // P0-PM-002: Show success toast and invalidate caches
        toast.success(`${bob_name} installed successfully!`, {
          description: 'View it in your instance.',
          action: {
            label: "What's Next?",
            onClick: () => {
              setGuidanceBobName(bob_name);
              setShowGuidance(true);
            }
          }
        });

        // P0-PM-002: Invalidate React Query caches to refresh data
        queryClient.invalidateQueries({ queryKey: ['instances', instanceId] });
        queryClient.invalidateQueries({ queryKey: ['bobs', 'installed', instanceId] });
        queryClient.invalidateQueries({ queryKey: ['my-instances'] });

        // P0-PM-004: Track installation success
        analytics.track('BoB Installation Completed', {
          bobName: bob_name,
          instanceId,
          success: true,
        });

        // P0-PM-005: Auto-show guidance for first installation
        const isFirstInstallation =
          Array.from(installations.values()).filter(i => i.state === 'installed').length === 0;

        if (isFirstInstallation) {
          setTimeout(() => {
            setGuidanceBobName(bob_name);
            setShowGuidance(true);
          }, 2000); // Show after 2 seconds
        }
      }
    });
  }, [events, instanceId, queryClient, navigate, onClose, onRetry]);

  // P0-PM-003 FIX: Check if all installations are complete or if there are active installations
  const allComplete = Array.from(installations.values()).every(
    (inst) => inst.state === "installed" || inst.state === "failed"
  );

  const hasActiveInstallations = Array.from(installations.values()).some(
    (inst) => inst.state !== "installed" && inst.state !== "failed"
  );

  const successCount = Array.from(installations.values()).filter(
    (inst) => inst.state === "installed"
  ).length;

  const failedCount = Array.from(installations.values()).filter(
    (inst) => inst.state === "failed"
  ).length;

  // P0-PM-003 FIX: Only allow background continuation when all installations complete
  useEffect(() => {
    if (allComplete && installations.size > 0) {
      setCanContinueInBackground(true);
    } else {
      setCanContinueInBackground(false);
    }
  }, [allComplete, installations.size]);

  const getStateIcon = (state: string) => {
    switch (state) {
      case "installed":
        return <CheckCircle2 className="h-5 w-5 text-emerald-400" />;
      case "failed":
        return <XCircle className="h-5 w-5 text-red-400" />;
      case "queued":
        return <Clock className="h-5 w-5 text-slate-400" />;
      default:
        return <Loader2 className="h-5 w-5 text-blue-400 animate-spin" />;
    }
  };

  const getStateLabel = (state: string): string => {
    switch (state) {
      case "queued":
        return "Queued";
      case "downloading":
        return "Downloading";
      case "downloaded":
        return "Downloaded";
      case "extracting":
        return "Extracting";
      case "installing":
        return "Installing";
      case "installed":
        return "Installed";
      case "failed":
        return "Failed";
      default:
        return state;
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  // P0-PM-003 FIX: Handle continue in background
  const handleContinueInBackground = () => {
    setUserDismissedModal(true);
    onClose();
  };

  return (
    <>
    <Dialog
      open={!userDismissedModal}
      onOpenChange={(open) => {
        // P0-PM-003 FIX: Only allow close if all installations are complete
        if (!open && canContinueInBackground) {
          onClose();
        }
        // Otherwise, ignore close attempt (blocks X button and ESC during active installations)
      }}
    >
      <DialogContent
        className="max-w-2xl"
        aria-labelledby="install-progress-title"
        aria-describedby="install-progress-description"
        closeLabel="Close installation progress dialog"
      >
        <DialogHeader>
          <DialogTitle id="install-progress-title">
            Installing BoBs
          </DialogTitle>
          <DialogDescription id="install-progress-description">
            {allComplete
              ? "Installation complete"
              : "Installing selected BoBs to your instance..."}
          </DialogDescription>
        </DialogHeader>

        {/* Connection status */}
        <div
          className="flex items-center gap-2 text-sm"
          role="status"
          aria-live="polite"
        >
          {isConnected ? (
            <>
              <div
                className="h-2 w-2 bg-green-500 rounded-full animate-pulse"
                aria-hidden="true"
              />
              <span className="text-green-400">Connected</span>
            </>
          ) : (
            <>
              <div
                className="h-2 w-2 bg-yellow-500 rounded-full animate-pulse"
                aria-hidden="true"
              />
              <span className="text-yellow-400">Connecting...</span>
            </>
          )}
        </div>

        {/* Connection error */}
        {connectionError && (
          <div
            className="p-3 bg-red-900/20 border border-red-800 rounded text-sm text-red-400"
            role="alert"
          >
            <p className="font-medium">Connection Error:</p>
            <p>{connectionError}</p>
          </div>
        )}

        {/* Installation list */}
        <div className="space-y-4 max-h-[400px] overflow-y-auto">
          {installations.size === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Loader2
                className="h-8 w-8 mx-auto mb-2 animate-spin"
                aria-hidden="true"
              />
              <p>Preparing installations...</p>
            </div>
          ) : (
            Array.from(installations.values()).map((installation) => (
              <div
                key={installation.bobName}
                className="rounded-lg border border-slate-700 p-4 bg-slate-800/30"
                role="article"
                aria-labelledby={`install-${installation.bobName}-title`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h3
                      id={`install-${installation.bobName}-title`}
                      className="font-medium truncate text-slate-100"
                    >
                      {installation.bobName}
                    </h3>
                    <p className="text-sm text-slate-400 capitalize">
                      {getStateLabel(installation.state)}
                    </p>
                  </div>
                  <div aria-hidden="true">
                    {getStateIcon(installation.state)}
                  </div>
                </div>

                {/* Progress bar */}
                {installation.progress !== undefined &&
                  installation.state !== "failed" && (
                    <div>
                      <Progress
                        value={installation.progress}
                        className="h-2"
                        aria-valuenow={installation.progress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`Installation progress for ${installation.bobName}: ${installation.progress}%`}
                      />
                      <div className="flex justify-between items-center mt-1">
                        <p className="text-xs text-slate-500">
                          {installation.bytesDownloaded &&
                          installation.totalBytes
                            ? `${formatBytes(installation.bytesDownloaded)} of ${formatBytes(installation.totalBytes)}`
                            : ""}
                        </p>
                        <p className="text-xs text-slate-500">
                          {installation.progress}%
                        </p>
                      </div>
                    </div>
                  )}

                {/* Error message */}
                {installation.error && (
                  <div
                    className="mt-2 p-3 bg-red-900/20 border border-red-800 rounded text-sm text-red-400"
                    role="alert"
                  >
                    <p className="font-medium">Error:</p>
                    <p>{installation.error}</p>
                  </div>
                )}

                {/* Retry button for failed installations */}
                {installation.state === "failed" && onRetry && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRetry(installation.bobName)}
                    className="mt-2 w-full"
                    aria-label={`Retry installation for ${installation.bobName}`}
                  >
                    <RotateCw className="h-4 w-4 mr-2" aria-hidden="true" />
                    Retry Installation
                  </Button>
                )}
              </div>
            ))
          )}
        </div>

        {/* Summary */}
        <div
          className="text-sm text-slate-400"
          role="status"
          aria-live="polite"
        >
          {allComplete ? (
            <>
              {successCount > 0 && (
                <span className="text-emerald-400">
                  {successCount} of {installations.size} BoB
                  {installations.size !== 1 ? "s" : ""} installed successfully
                </span>
              )}
              {failedCount > 0 && (
                <span className="text-red-400 ml-2">
                  {failedCount} failed
                </span>
              )}
            </>
          ) : (
            <>
              Installing {installations.size} BoB
              {installations.size !== 1 ? "s" : ""}...
            </>
          )}
        </div>

        <DialogFooter>
          {canContinueInBackground ? (
            // P0-PM-003 FIX: All complete - show "Done" button
            <Button onClick={onClose}>
              <Check className="h-4 w-4 mr-2" aria-hidden="true" />
              Done
            </Button>
          ) : (
            // P0-PM-003 FIX: Still installing - show "Continue in Background" button
            // This closes modal but doesn't stop installations
            <Button
              variant="outline"
              onClick={handleContinueInBackground}
              disabled={installations.size === 0}
              aria-label="Continue installation in background"
            >
              Continue in Background
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* P0-PM-005: Post-install guidance modal */}
    {showGuidance && (
      <PostInstallGuidance
        bobName={guidanceBobName}
        instanceId={instanceId}
        onClose={() => setShowGuidance(false)}
      />
    )}
    </>
  );
}
